// src/components/AddSale.jsx
import { useState, useEffect } from "react";
import { FaTimes, FaCalendarAlt, FaStickyNote, FaUser } from "react-icons/fa";
import Select from "react-select";
import api from "../utils/axios";
import toast from "react-hot-toast";

export default function AddSale({ onClose, users, preselectedUser, onSaleAdded }) {
  const [selectedUser, setSelectedUser] = useState(
    preselectedUser
      ? {
          value: preselectedUser._id,
          label: `${preselectedUser.name} (${preselectedUser.mobile})`,
        }
      : null
  );

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [quantity, setQuantity] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  // ── DATE RESTRICTION LOGIC ──
  // Pichhle mahine ka sale sirf current mahine ke pehle 10 din tak allowed
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const currentDay = today.getDate(); // aaj ka din (1 to 31)

  // Min date = pichhle mahine ka 1st day
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  lastMonthStart.setHours(0, 0, 0, 0);

  // Agar aaj mahine ka 11 ya usse zyada din hai → pichhla mahina band
  const effectiveMinDate = currentDay >= 11 ? new Date(today.getFullYear(), today.getMonth(), 1) : lastMonthStart;

  const minDateStr = effectiveMinDate.toISOString().split("T")[0];
  const maxDateStr = today.toISOString().split("T")[0];

  // Help text ke liye month names
  const currentMonthName = today.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const lastMonthName = lastMonthStart.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  // Prepare user options
  const userOptions = users.map((user) => ({
    value: user._id,
    label: `${user.name} (${user.mobile})`,
  }));

  useEffect(() => {
    if (preselectedUser) {
      setSelectedUser({
        value: preselectedUser._id,
        label: `${preselectedUser.name} (${preselectedUser.mobile})`,
      });
    }
  }, [preselectedUser]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#facc15" : "#d1d5db",
      borderRadius: "0.75rem",
      padding: "0.25rem",
      minHeight: "44px",
      boxShadow: "none",
      "&:hover": { borderColor: "#facc15" },
    }),
    valueContainer: (provided) => ({ ...provided, padding: "0 8px" }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    placeholder: (provided) => ({ ...provided, fontSize: "0.875rem" }),
    singleValue: (provided) => ({ ...provided, fontSize: "0.875rem" }),
    menu: (provided) => ({ ...provided, borderRadius: "0.75rem", marginTop: "0.5rem", zIndex: 60 }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "0.875rem",
      backgroundColor: state.isSelected ? "#fde68a" : state.isFocused ? "#fffbeb" : "white",
      color: state.isSelected ? "#111827" : "inherit",
    }),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser?.value) return toast.error("Please select a user");
    if (!quantity || parseFloat(quantity) < 0.01)
      return toast.error("Please enter a valid quantity (minimum 0.01 KG)");

    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    // Final strict check
    if (selectedDate < effectiveMinDate) {
      return toast.error(
        `You can only add sales from ${effectiveMinDate.toLocaleDateString("en-IN", {
          month: "long",
          year: "numeric",
        })} onwards.`
      );
    }
    if (selectedDate > today) {
      return toast.error("Future dates are not allowed.");
    }

    setLoading(true);

    try {
      const saleData = {
        userId: selectedUser.value,
        date: selectedDate.toISOString(),
        quantity: parseFloat(quantity),
        remarks: remarks.trim(),
      };

      await api.post("/sales/add-sale", saleData);

      toast.success("Sale added successfully!");
      onSaleAdded?.(); // parent ko refresh karne ke liye (optional)
      onClose();       // modal band kar do
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to add sale. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={loading ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col animate-scaleIn">
        {/* Header */}
        <div className="px-4 sm:px-8 py-3 sm:py-6 border-b flex justify-between items-center">
          <h2 className="text-base sm:text-2xl font-extrabold text-gray-900">Add New Sale</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="h-9 w-9 rounded-lg grid place-items-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition active:scale-95"
            aria-label="Close"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* User */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FaUser /> Customer *
              </label>

              {preselectedUser ? (
                <div className="px-4 py-3 rounded-xl bg-yellow-50 border border-yellow-300 text-gray-800 font-semibold text-sm flex items-center gap-2">
                  <FaUser className="text-yellow-600" />
                  {preselectedUser.name} ({preselectedUser.mobile})
                </div>
              ) : (
                <Select
                  options={userOptions}
                  value={selectedUser}
                  onChange={setSelectedUser}
                  placeholder="Search user by name or mobile..."
                  isDisabled={!!preselectedUser}
                  isSearchable={!preselectedUser}
                  styles={customStyles}
                  noOptionsMessage={() => "No users found"}
                />
              )}
            </div>

            {/* Date */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FaCalendarAlt /> Sale Date *
              </label>
              <input
                type="date"
                value={date}
                min={minDateStr} // pichhle mahine ka 1st ya current month ka 1st
                max={maxDateStr} // aaj tak
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-600 mt-1">
                Allowed: {currentDay <= 10 ? `${lastMonthName} aur ` : ""}{currentMonthName} tak
              </p>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Quantity (KG) *
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm"
                required
                min="0.01"
                step="0.01"
                placeholder="e.g. 150.50"
                disabled={loading}
              />
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FaStickyNote /> Remarks (Optional)
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none text-sm"
                rows={3}
                placeholder="TMT/Structure or any other remarks"
                disabled={loading}
              />
            </div>

            <button type="submit" className="hidden" />
          </form>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-8 py-3 sm:py-4 border-t bg-white">
          <div className="flex gap-3 sm:gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 h-11 px-4 sm:px-6 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition text-sm sm:text-base"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`flex-1 h-11 px-4 sm:px-6 rounded-xl font-semibold transition text-sm sm:text-base ${
                loading
                  ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                  : "bg-yellow-600 text-white hover:bg-yellow-700"
              }`}
            >
              {loading ? "Adding Sale..." : "Add Sale"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}